import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';

const PasswordUpdate = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isValidToken, setIsValidToken] = useState(false);
    const [isCheckingToken, setIsCheckingToken] = useState(true);
    
    const navigate = useNavigate();

    // Check if we have a valid recovery token in the URL
    useEffect(() => {
        const checkToken = async () => {
            try {
                const { data, error } = await supabase.auth.getSession();
                if (data.session?.user) {
                    setIsValidToken(true);
                } else {
                    setError('Invalid or expired reset link. Please request a new password reset.');
                }
            } catch (err) {
                setError('Invalid or expired reset link. Please request a new password reset.');
            } finally {
                setIsCheckingToken(false);
            }
        };
        
        checkToken();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        
        // Validate passwords
        if (!password.trim()) {
            setError('Please enter a new password');
            return;
        }
        
        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }
        
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        setIsLoading(true);
        
        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });
            
            if (error) {
                setError(error.message);
            } else {
                setSuccess('Password updated successfully! Redirecting to login...');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }
        } catch (err) {
            setError('Failed to update password. Please try again.');
            console.error('Password update error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    if (isCheckingToken) {
        return (
            <Container fluid className="py-5 px-3">
                <Row className="justify-content-center m-0">
                    <Col md={6} lg={4}>
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <h3 className="mt-3">Verifying reset link...</h3>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (!isValidToken) {
        return (
            <Container fluid className="py-5 px-3">
                <Row className="justify-content-center m-0">
                    <Col md={6} lg={4}>
                        <div className="text-center mb-4">
                            <h2>Invalid Reset Link</h2>
                            <p className="text-muted">The password reset link is invalid or has expired.</p>
                        </div>
                        
                        {error && <Alert variant="danger">{error}</Alert>}
                        
                        <div className="text-center">
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/password-reset')}
                                className="auth-primary-btn"
                            >
                                Request New Reset Link
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="py-5 px-3">
            <Row className="justify-content-center m-0">
                <Col md={6} lg={4}>
                    <div className="text-center mb-4">
                        <h2>Update Password</h2>
                        <p className="text-muted">Enter your new password below</p>
                    </div>

                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Form onSubmit={handleSubmit} className="auth-form">
                        <Form.Group className="mb-3">
                            <Form.Label className="d-block text-start">New Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="Enter new password"
                                className="form-control-custom"
                                minLength={6}
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="d-block text-start">Confirm New Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                placeholder="Confirm new password"
                                className="form-control-custom"
                                minLength={6}
                            />
                        </Form.Group>

                        <Button 
                            variant="primary" 
                            type="submit" 
                            className="auth-primary-btn mb-3"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Updating...' : 'Update Password'}
                        </Button>

                        <div className="text-center">
                            Remember your password? <a href="/login" className="auth-link">Log in</a>
                        </div>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default PasswordUpdate;